import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';

import { withExperiment } from '@sparefoot/react-core/decorators';
import { getPaidLandingViewPixel } from '@sparefoot/react-core/utils/tracking';

import { getTollfreePhoneNumber as getTollfreePhoneNumberAction } from 'store/modules/phone';
import marketingSelector from 'store/selectors/marketing';

import { phonePropType } from 'config/propTypes';
import stepsCards from 'config/stepsCards';
import { PLPReviews } from 'config/reviews';

import { Page } from 'components/core/Page';
import { TrustPilotReviewSection } from 'components/landing';
import { MarketingFooter } from 'components/landing/MarketingFooter';
import { StickySearch } from 'components/search/StickySearch';
import { MarketingHero } from 'components/landing/MarketingHero';
import { MarketingSimpleSteps } from 'components/landing/MarketingSimpleSteps';
import { MarketingPartners } from 'components/landing/MarketingPartners';

import useSearchHandler from 'hooks/useSearchHandler';
import usePageViewTracking from 'hooks/usePageViewTracking';

import './MarketingLandingPage.scss';
import { PAGE_TYPES } from '@sparefoot/tracking-events';

import { compose } from 'redux';

export function MarketingLandingPage({
	additionalSearchParams: additionalSearchParamsProp,
	handleSearch: handleSearchProp,
	sf14850Variation,
	phone: phoneProp,
	actions: actionsProp,
}) {
	const dispatch = useDispatch();
	const storeProps = useSelector(marketingSelector);

	const phone = phoneProp === undefined ? storeProps.phone : phoneProp;
	const actions = useMemo(
		() =>
			actionsProp ?? {
				getTollfreePhoneNumber: (...args) =>
					dispatch(getTollfreePhoneNumberAction(...args)),
			},
		[actionsProp, dispatch],
	);

	const searchHandler = useSearchHandler({
		additionalSearchParams: additionalSearchParamsProp,
	});
	const handleSearch = handleSearchProp ?? searchHandler;

	usePageViewTracking(PAGE_TYPES.PPC);

	const trackingPixel = useMemo(() => getPaidLandingViewPixel('toplvl'), []);

	return (
		<Page
			className={classnames('marketing-landing-page')}
			landingPage
			marketingPage
			trackingPixel={trackingPixel}
			pageType={PAGE_TYPES.PPC}
			noIndex
			noFollow
		>
			<MarketingHero
				sf14850Variation={sf14850Variation}
				onSearch={handleSearch}
				phone={phone}
				getTollfreePhoneNumber={actions.getTollfreePhoneNumber}
			/>
			<StickySearch
				hidden
				horizontal
				additionalSearchParams={additionalSearchParamsProp}
				onSearch={handleSearch}
				callToBook
				phone={phone}
				getTollfreePhoneNumber={actions.getTollfreePhoneNumber}
			/>
			<MarketingSimpleSteps stepsCards={stepsCards} />
			<TrustPilotReviewSection
				customHeader
				isHomePage={false}
				reviews={PLPReviews}
				className="marketing-reviews"
			/>
			<MarketingPartners />
			<MarketingFooter className="blue-marketing-footer" />
		</Page>
	);
}

MarketingLandingPage.propTypes = {
	actions: PropTypes.shape({
		getTollfreePhoneNumber: PropTypes.func.isRequired,
	}),
	additionalSearchParams: PropTypes.object,
	handleSearch: PropTypes.func,
	phone: phonePropType,
	sf14850Variation: PropTypes.string,
};

export default compose(
	withExperiment('sf_14850_hero_promo_test_ssdc'),
	withExperiment('sf_15731_plp_refactor_ssdc'),
)(MarketingLandingPage);
